import { useEffect } from "react";

import {
  dismissBannerByKey,
  useDismissibleBanner
} from "hooks/use-dismissible-banner.hook";
import useLatestRef from "hooks/use-latest-ref.hook";

import {
  MENTIONS_LAUNCH_KEYS,
  MENTIONS_USED_EVENT,
  MENTION_BUTTON_HINT_BODY_CLASS,
  MENTION_BUTTON_USED_EVENT
} from "./mentions-launch";
import useMentionsLaunchUserId from "./use-mentions-launch-user-id.hook";

/**
 * Mounted once in the app shell. Renders nothing; it:
 * - hides every writer-side hint (chart notes banner, "@" button dot, comment box dot) the first
 *   time the person picks a teammate in any @ picker, wherever they are in the app;
 * - hides the "@" button dot when that button is clicked;
 * - puts MENTION_BUTTON_HINT_BODY_CLASS on <body> while the "@" button dot is due. The button lives
 *   in TinyMCE's own toolbar, outside React, so the dot is drawn by CSS (word-processor.css).
 *
 * Flag off, or before the signed-in user is known: renders nothing and touches no storage.
 */
export default function MentionsLaunchController() {
  const userId = useMentionsLaunchUserId();
  return userId ? <Controller userId={userId} /> : null;
}

function Controller({ userId }: { userId: string }) {
  // The "@" dot is drawn by CSS on every editor, so this instance is what decides whether it shows.
  // Its 30-day clock therefore starts at the first app load after launch, and it reports no
  // `shown` (nothing here knows whether an editor is open).
  const editorButton = useDismissibleBanner({
    storageKey: MENTIONS_LAUNCH_KEYS.editorButton,
    userId,
    syncAcrossInstances: true,
    datadogActions: { dismissed: "mentions_editor_button_hint_dismissed" }
  });
  const editorButtonRef = useLatestRef(editorButton);

  useEffect(() => {
    const onUsed = () => {
      // The banner and the comment hint are rendered elsewhere. Dismissing them by key (rather than
      // through hook instances here) keeps them from being "first seen" on app load, and reports a
      // dismissal only for one the person had actually been shown.
      dismissBannerByKey({
        storageKey: MENTIONS_LAUNCH_KEYS.banner,
        userId,
        datadogAction: "mentions_launch_banner_dismissed",
        context: { source: "used_feature" }
      });
      dismissBannerByKey({
        storageKey: MENTIONS_LAUNCH_KEYS.commentBox,
        userId,
        datadogAction: "mentions_comment_hint_dismissed",
        context: { source: "used_feature" }
      });
      if (editorButtonRef.current.visible) {
        editorButtonRef.current.dismiss({ source: "used_feature" });
      }
    };
    const onButtonUsed = () => {
      if (editorButtonRef.current.visible) {
        editorButtonRef.current.dismiss({ source: "used_control" });
      }
    };
    window.addEventListener(MENTIONS_USED_EVENT, onUsed);
    window.addEventListener(MENTION_BUTTON_USED_EVENT, onButtonUsed);
    return () => {
      window.removeEventListener(MENTIONS_USED_EVENT, onUsed);
      window.removeEventListener(MENTION_BUTTON_USED_EVENT, onButtonUsed);
    };
  }, [editorButtonRef, userId]);

  useEffect(() => {
    if (!editorButton.visible) return undefined;
    document.body.classList.add(MENTION_BUTTON_HINT_BODY_CLASS);
    return () => document.body.classList.remove(MENTION_BUTTON_HINT_BODY_CLASS);
  }, [editorButton.visible]);

  return null;
}
